import { useState } from 'react';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useRatings, type EntityType } from '@/hooks/community/useRatings';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface RatingStarsProps {
  entityType: EntityType;
  entityId: string;
  compact?: boolean;
}

export function RatingStars({ entityType, entityId, compact = false }: RatingStarsProps) {
  const [hoveredRating, setHoveredRating] = useState(0);
  const { userRating, aggregate, loading, submitRating } = useRatings(entityType, entityId);
  const { user } = useAuth();

  const handleRatingClick = (rating: number) => {
    submitRating(rating);
  };

  const renderStars = (rating: number, interactive = false, size = 'default') => {
    const starSize = size === 'small' ? 'h-3 w-3' : size === 'large' ? 'h-6 w-6' : 'h-4 w-4';
    
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={cn(
              starSize,
              'cursor-pointer transition-colors',
              star <= (interactive ? (hoveredRating || rating) : rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground',
              interactive && user && 'hover:text-yellow-400'
            )}
            onClick={() => interactive && handleRatingClick(star)}
            onMouseEnter={() => interactive && setHoveredRating(star)}
            onMouseLeave={() => interactive && setHoveredRating(0)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
        {!compact && <Skeleton className="h-20 w-full" />}
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        {renderStars(userRating?.rating || Math.round(aggregate?.average_rating || 0), true, 'small')}
        <span className="text-muted-foreground">
          {aggregate?.average_rating?.toFixed(1) || '0.0'} ({aggregate?.total_ratings || 0})
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Community Rating</h3>
        {aggregate && (
          <div className="text-right">
            <div className="text-2xl font-bold">{aggregate.average_rating.toFixed(1)}</div>
            <div className="text-sm text-muted-foreground">{aggregate.total_ratings} ratings</div>
          </div>
        )}
      </div>

      {/* Interactive rating */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Your rating:</span>
          {renderStars(userRating?.rating || 0, !loading && !!user, 'large')}
        </div>
        
        {!user && (
          <p className="text-sm text-muted-foreground">
            Sign in to rate this {entityType}
          </p>
        )}
      </div>

      {/* Rating distribution */}
      {aggregate && aggregate.total_ratings > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Rating Distribution</h4>
          <div className="space-y-1">
            {[5, 4, 3, 2, 1].map((stars) => {
              const count = aggregate[`distribution_${stars}` as keyof typeof aggregate] as number;
              const percentage = (count / aggregate.total_ratings) * 100;
              
              return (
                <div key={stars} className="flex items-center gap-2 text-sm">
                  <span className="w-8">{stars}★</span>
                  <Progress value={percentage} className="flex-1 h-2" />
                  <span className="w-8 text-muted-foreground">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {aggregate?.total_ratings === 0 && (
        <div className="text-center py-6 text-muted-foreground">
          <Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Be the first to rate this {entityType}!</p>
        </div>
      )}
    </div>
  );
}